import type { MeritBadgeRequirement } from '@appTypes/esb';
import type { UpdateBulkMeritBadgeRequirementsResponse } from '@appTypes/esb/advancements/v2/youth/meritBadges/_meritBadgeId/requirements';

import { sanitizeErrorMessage } from './errorMessages';
import type {
  GroupedError,
  RequirementError,
  YouthSubmitResult,
} from './types';

/**
 * Groups requirement errors by message, collecting the requirement labels
 * for each distinct message.
 */
export function groupErrorsByMessage(
  errors: RequirementError[],
  requirementById: Map<number, MeritBadgeRequirement>,
): GroupedError[] {
  const grouped = new Map<string, string[]>();

  for (const err of errors) {
    const req = requirementById.get(err.requirementId);
    const reqLabel = req
      ? req.requirementNumber || req.listNumber
      : `#${err.requirementId}`;
    const existing = grouped.get(err.message);
    if (existing) {
      existing.push(reqLabel);
    } else {
      grouped.set(err.message, [reqLabel]);
    }
  }

  return [...grouped.entries()].map(([message, reqLabels]) => ({
    message,
    reqLabels,
  }));
}

export function parseSubmitResponse(
  response: UpdateBulkMeritBadgeRequirementsResponse[],
): YouthSubmitResult[] {
  return response.map(userResult => ({
    userId: userResult.userId,
    successCount: userResult.requirements.filter(
      req => req.status === 'Success',
    ).length,
    errors: userResult.requirements
      .filter(req => req.status === 'Fail' && req.message)
      .map(req => ({
        requirementId: req.id,
        message: sanitizeErrorMessage(req.message),
      })),
  }));
}

/**
 * Per-youth results of a bulk requirements POST that partially failed,
 * or `null` when the error carries no per-requirement failures
 */
export function parseSubmitError(error: unknown): YouthSubmitResult[] | null {
  const data = (error as { data?: unknown })?.data;
  if (!Array.isArray(data)) return null;

  const results = parseSubmitResponse(
    data as UpdateBulkMeritBadgeRequirementsResponse[],
  );
  return results.some(result => result.errors.length > 0) ? results : null;
}
